import React, {useEffect, useRef, useState} from 'react';

type Props = {
  banner?: string;
  title: string;
  languages: React.ReactNode;
  language: string;
  onHome: () => void;
  onBack?: () => void;
  showActions?: boolean;
  showLanguages?: boolean;
};

export const KioskBottomBar = ({
  banner,
  title,
  languages,
  language,
  onHome,
  onBack,
  showActions = true,
  showLanguages = true,
}: Props) => (
  <BottomBarContent
    banner={banner}
    title={title}
    languages={languages}
    language={language}
    onHome={onHome}
    onBack={onBack}
    showActions={showActions}
    showLanguages={showLanguages}
  />
);

const BottomBarContent = ({
  banner,
  title,
  languages,
  language,
  onHome,
  onBack,
  showActions = true,
  showLanguages = true,
}: Props) => {
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [cacheProgress, setCacheProgress] = useState({
    percent: 100,
    label: 'Cache offline concluída',
  });
  const [online, setOnline] = useState(navigator.onLine);
  const [storageText, setStorageText] = useState('');
  const clickTimes = useRef<number[]>([]);
  const lastSync =
    typeof window !== 'undefined'
      ? window.localStorage.getItem('findify-last-content-sync')
      : null;

  useEffect(() => {
    const updateOnline = () => setOnline(navigator.onLine);
    const updateProgress = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail) setCacheProgress(detail);
    };
    const refreshComplete = () => setRefreshing(false);
    const refreshFailed = () => setRefreshing(false);
    window.addEventListener('online', updateOnline);
    window.addEventListener('offline', updateOnline);
    window.addEventListener('findify-cache-progress', updateProgress);
    window.addEventListener('findify-cache-refresh-complete', refreshComplete);
    window.addEventListener('findify-cache-refresh-failed', refreshFailed);
    if (navigator.storage?.estimate) {
      void navigator.storage.estimate().then(({usage, quota}) => {
        if (usage && quota)
          setStorageText(
            `${Math.round(usage / 1024 / 1024)} MB / ${Math.round(quota / 1024 / 1024)} MB`,
          );
      });
    }
    return () => {
      window.removeEventListener('online', updateOnline);
      window.removeEventListener('offline', updateOnline);
      window.removeEventListener('findify-cache-progress', updateProgress);
      window.removeEventListener(
        'findify-cache-refresh-complete',
        refreshComplete,
      );
      window.removeEventListener('findify-cache-refresh-failed', refreshFailed);
    };
  }, []);

  const handleLogoClick = () => {
    const now = Date.now();
    clickTimes.current = [
      ...clickTimes.current.filter(time => now - time < 900),
      now,
    ];
    if (clickTimes.current.length >= 3) {
      clickTimes.current = [];
      setMaintenanceOpen(true);
    }
  };

  const forceRefresh = async () => {
    if (!online) return;
    setRefreshing(true);
    try {
      const registrations = await navigator.serviceWorker?.getRegistrations();
      await Promise.all(
        (registrations ?? []).map(registration => registration.update()),
      );
      setCacheProgress({percent: 0, label: 'A iniciar a atualização'});
      window.dispatchEvent(new Event('findify-force-content-refresh'));
    } finally {
      // The application signals completion after saving the full snapshot.
    }
  };

  return (
    <>
      {showActions && (
        <div className="kiosk-bottom-bar__actions">
          <button className="category__home" onClick={onHome}>
            ⌂ {language === 'en-US' ? 'Main menu' : 'Menu principal'}
          </button>
          {onBack && (
            <button className="kiosk-bottom-bar__back" onClick={onBack}>
              ← {language === 'en-US' ? 'Back' : 'Voltar'}
            </button>
          )}
        </div>
      )}
      <div className="home-footer">
        {banner && <img src={banner} alt={title} onClick={handleLogoClick} />}
        {showLanguages && languages}
      </div>
      {maintenanceOpen && (
        <div
          className="kiosk-maintenance"
          role="dialog"
          aria-label="Atualização de conteúdos">
          <div className="kiosk-maintenance__panel">
            <button
              className="kiosk-maintenance__close"
              onClick={() => setMaintenanceOpen(false)}>
              ×
            </button>
            <strong>
              {language === 'en-US'
                ? 'Content update'
                : 'Atualização de conteúdos'}
            </strong>
            <p>
              {lastSync
                ? `${language === 'en-US' ? 'Last update: ' : 'Última atualização: '}${new Date(lastSync).toLocaleString(language)}`
                : language === 'en-US'
                  ? 'No update recorded on this device.'
                  : 'Ainda não existe uma atualização registada neste dispositivo.'}
            </p>
            <button
              className="kiosk-maintenance__refresh"
              disabled={refreshing || !online}
              onClick={() => void forceRefresh()}>
              {refreshing
                ? language === 'en-US'
                  ? 'Updating…'
                  : 'A atualizar…'
                : language === 'en-US'
                  ? 'Force full update'
                  : 'Forçar atualização integral'}
            </button>
            <div className="kiosk-maintenance__progress" aria-live="polite">
              <div className="kiosk-maintenance__progress-label">
                <span>{cacheProgress.label}</span>
                <span>{cacheProgress.percent}%</span>
              </div>
              <div className="kiosk-maintenance__progress-track">
                <div
                  className="kiosk-maintenance__progress-value"
                  style={{width: `${cacheProgress.percent}%`}}
                />
              </div>
            </div>
            <small>
              {online
                ? language === 'en-US'
                  ? 'Network: connected'
                  : 'Rede: ligada'
                : language === 'en-US'
                  ? 'Network: offline'
                  : 'Rede: desligada'}
              {storageText ? ` · ${storageText}` : ''}
            </small>
            {!online && (
              <small>
                {language === 'en-US'
                  ? 'Connect to the network to update.'
                  : 'Ligue o dispositivo à rede para atualizar.'}
              </small>
            )}
          </div>
        </div>
      )}
    </>
  );
};
